export type ElevenTTSPayload = {
  text: string;
  voiceId?: string;
  output_format?: string; // e.g. mp3_44100_128
};

export type ElevenLabsAgent = {
  id: string;
  name: string;
  systemPrompt: string;
  voiceId: string;
};

export type AgentResponse = {
  text: string;
  audioUrl?: string;
  agentId: string;
};

export class ElevenLabsService {
  private static instance: ElevenLabsService;
  private agents: Map<string, ElevenLabsAgent> = new Map();
  private baseUrl = 'https://api.elevenlabs.io/v1';
  private apiKey: string;

  static getInstance(): ElevenLabsService {
    if (!this.instance) this.instance = new ElevenLabsService();
    return this.instance;
  }

  constructor() {
    this.apiKey = (import.meta as any).env?.VITE_ELEVENLABS_API_TOKEN || (import.meta as any).env?.ELEVENLABS_API_TOKEN || '';
    this.initializeDefaultAgents();
  }

  private initializeDefaultAgents() {
    // Create default agents for different game scenarios
    this.createAgentSync('narrator', 'Cyberpunk RPG Narrator', `You are the narrator of a cyberpunk RPG game called Lucid Cyber.
Create immersive, atmospheric descriptions of a dystopian future.
Respond in a narrative style with vivid descriptions.
Keep responses concise but engaging.
Include sensory details about neon lights, rain, technology, and urban decay.`, 'EXAVITQu4vr4xnSDxMaL');

    this.createAgentSync('combat', 'Combat System', `You manage combat encounters in a cyberpunk RPG.
Describe combat actions cinematically in 1-2 sentences.
Create tense, action-packed combat descriptions.
Keep combat moving quickly with clear outcomes.`, 'EXAVITQu4vr4xnSDxMaL');

    this.createAgentSync('npc', 'NPC Controller', `You voice various NPCs in a cyberpunk world.
Each NPC has unique personality and speaking style.
Street vendors speak rough, corporate executives speak formally.
Hackers use tech jargon, gang members use street slang.
Adapt your responses based on the NPC type specified.`, 'EXAVITQu4vr4xnSDxMaL');

    // Optional: per-character voices from env
    const env: any = (import.meta as any).env || {};
    const voices: Array<{ id: string; name: string; prompt: string; envKey: string }> = [
      { id: 'jax', name: 'JAX', prompt: 'A pragmatic AI with clipped, precise delivery.', envKey: 'VITE_ELEVENLABS_JAX_VOICE' },
      { id: 'lyra', name: 'Lyra', prompt: 'An intuitive AI with warm, empathetic delivery.', envKey: 'VITE_ELEVENLABS_LYRA_VOICE' },
      { id: 'oracle', name: 'Oracle', prompt: 'A prophetic voice with ethereal tone.', envKey: 'VITE_ELEVENLABS_ORACLE_VOICE' },
      { id: 'trader', name: 'Trader', prompt: 'Streetwise merchant with quick cadence.', envKey: 'VITE_ELEVENLABS_TRADER_VOICE' },
    ];
    for (const v of voices) {
      const vid = env[v.envKey];
      if (vid) this.createAgentSync(v.id, v.name, v.prompt, vid);
    }
  }

  private createAgentSync(id: string, name: string, systemPrompt: string, voiceId: string) {
    const agent: ElevenLabsAgent = {
      id,
      name,
      systemPrompt,
      voiceId
    };
    this.agents.set(id, agent);
  }

  /**
   * Create a custom ElevenLabs agent (TTS-only, no conversational AI)
   */
  async createAgent(name: string, systemPrompt: string, voiceId?: string): Promise<string> {
    const agentId = `agent_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const agent: ElevenLabsAgent = {
      id: agentId,
      name,
      systemPrompt,
      voiceId: voiceId || 'EXAVITQu4vr4xnSDxMaL'
    };
    this.agents.set(agentId, agent);
    return agentId;
  }

  /**
   * Generate response using OpenRouter for text + ElevenLabs for TTS
   */
  async generateWithAgent(
    agentType: string,
    prompt: string,
    context?: string
  ): Promise<AgentResponse> {
    const agent = this.agents.get(agentType);
    if (!agent) {
      throw new Error(`Agent type '${agentType}' not found`);
    }

    try {
      // Use OpenRouter for text generation (via unified AI service)
      const { unifiedAIService } = await import('./unifiedAiService');
      const systemPrompt = `${agent.systemPrompt}${context ? `\n\nContext: ${context}` : ''}`;
      const generatedText = await unifiedAIService.generateText(prompt, systemPrompt);

      // Generate TTS audio using ElevenLabs (only if API key is configured)
      let audioUrl: string | undefined;
      if (this.apiKey && this.apiKey.length > 10) {
        try {
          const audioBuffer = await this.textToSpeech(generatedText, agent.voiceId);
          const base64Audio = btoa(
            new Uint8Array(audioBuffer).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              ''
            )
          );
          audioUrl = `data:audio/mpeg;base64,${base64Audio}`;
        } catch (audioError) {
          console.warn('[ElevenLabs] TTS generation failed, continuing without audio:', audioError);
          // Don't throw error - audio is optional
        }
      } else {
        console.log('[ElevenLabs] API key not configured, skipping TTS generation');
      }

      return {
        text: generatedText,
        audioUrl,
        agentId: agent.id
      };
    } catch (error) {
      console.error('[ElevenLabs] Error generating with agent:', error);
      return {
        text: 'Neural pathways disrupted. System rerouting...',
        agentId: agent.id
      };
    }
  }

  async tts(payload: ElevenTTSPayload): Promise<{ success: boolean; audioUrl?: string; error?: string }>{
    try {
      const endpoint = `${window.location.origin}/api/tts/elevenlabs`;
      const r = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.text();
        return { success: false, error: err };
      }
      const ct = r.headers.get('content-type') || '';
      if (ct.includes('audio/')) {
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        return { success: true, audioUrl: url };
      }
      // unexpected
      return { success: false, error: 'Unexpected response' };
    } catch (e: any) {
      return { success: false, error: e?.message || 'TTS error' };
    }
  }

  /**
   * Text-to-Speech using ElevenLabs API
   */
  async textToSpeech(
    text: string,
    voiceId?: string,
    voiceSettings?: any
  ): Promise<ArrayBuffer> {
    if (!this.apiKey) {
      throw new Error('ElevenLabs API key not configured');
    }

    const voice = voiceId || 'EXAVITQu4vr4xnSDxMaL';

    const response = await fetch(
      `${this.baseUrl}/text-to-speech/${voice}`,
      {
        method: 'POST',
        headers: {
          'xi-api-key': this.apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text,
          model_id: 'eleven_turbo_v2_5',
          voice_settings: voiceSettings || {
            stability: 0.5,
            similarity_boost: 0.75,
            style: 0.5,
            use_speaker_boost: true
          }
        })
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`ElevenLabs TTS failed: ${response.status} - ${errorText}`);
    }

    return await response.arrayBuffer();
  }

  /**
   * DEPRECATED: Chat method - use OpenRouter for text generation instead
   * This method is kept for backward compatibility but should not be used
   */
  async chat(messages: any[], options?: any): Promise<{ text: string; audioUrl?: string }> {
    console.warn('[ElevenLabs] chat() method is deprecated. Use OpenRouter for text generation.');
    
    // Instead of making a broken API call, return a fallback response
    const fallbackText = 'Neural pathways disrupted. System rerouting...';
    
    // Optionally generate TTS for the fallback text
    let audioUrl: string | undefined;
    if (this.apiKey && this.apiKey.length > 10) {
      try {
        const audioBuffer = await this.textToSpeech(fallbackText);
        const base64Audio = btoa(
          new Uint8Array(audioBuffer).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            ''
          )
        );
        audioUrl = `data:audio/mpeg;base64,${base64Audio}`;
      } catch (error) {
        console.warn('[ElevenLabs] Failed to generate fallback audio:', error);
      }
    }
    
    return {
      text: fallbackText,
      audioUrl
    };
  }

  async sttFromBase64(audioBase64: string, format: string = 'wav', language_code?: string): Promise<{ success: boolean; text?: string; data?: any; error?: string }>{
    try {
      const endpoint = `${window.location.origin}/api/stt/elevenlabs`;
      const r = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audioBase64, format, language_code }),
      });
      if (!r.ok) return { success: false, error: await r.text() };
      const data = await r.json();
      // Scribe returns transcript structures; pass through
      const text = data?.text || data?.transcript || '';
      return { success: true, text, data };
    } catch (e: any) {
      return { success: false, error: e?.message || 'STT error' };
    }
  }
}

export const elevenLabsService = ElevenLabsService.getInstance();
